import { useState } from 'react';

import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { useInvalidatePendingMessageQueries } from '@apps/web/swr_data_fetching/use_pending_message_queries_refresh';
import { type StagedMessage } from '@libraries/typescript/api_client';

type UseStagedMessageActionsOptions = {
    readonly organizationId: string;
    readonly onUpdate?: () => void;
};

export function useStagedMessageActions({
    organizationId,
    onUpdate,
}: UseStagedMessageActionsOptions) {
    const [approvingMessageId, setApprovingMessageId] = useState<string | null>(
        null,
    );
    const [rejectingMessageId, setRejectingMessageId] = useState<string | null>(
        null,
    );
    const invalidatePendingMessageQueries =
        useInvalidatePendingMessageQueries();

    const handleApprove = async (
        stagedMessage: StagedMessage,
        editedContent?: string,
    ) => {
        setApprovingMessageId(stagedMessage.id);
        try {
            await getApiClient().patch({
                url: '/organizations/{organization_id}/conversations/{conversation_id}/staged-messages/{staged_message_id}' as const,
                pathParams: {
                    organization_id: organizationId,
                    conversation_id: stagedMessage.conversation_id,
                    staged_message_id: stagedMessage.id,
                },
                body: {
                    status: 'approved',
                    updated_content: editedContent,
                },
            });

            toast.success('Message approved and sent', {
                persistNotification: true,
                notificationType: 'message_action',
            });
            invalidatePendingMessageQueries();
            onUpdate?.();
        } catch (error) {
            console.error('Error approving staged message:', error);
            toast.error('Failed to approve message', {
                persistNotification: true,
                notificationType: 'message_action',
            });
        } finally {
            setApprovingMessageId(null);
        }
    };

    const handleReject = async (stagedMessage: StagedMessage) => {
        setRejectingMessageId(stagedMessage.id);
        try {
            await getApiClient().patch({
                url: '/organizations/{organization_id}/conversations/{conversation_id}/staged-messages/{staged_message_id}' as const,
                pathParams: {
                    organization_id: organizationId,
                    conversation_id: stagedMessage.conversation_id,
                    staged_message_id: stagedMessage.id,
                },
                body: {
                    status: 'rejected',
                },
            });

            toast.success('Message rejected', {
                persistNotification: true,
                notificationType: 'message_action',
            });
            invalidatePendingMessageQueries();
            onUpdate?.();
        } catch (error) {
            console.error('Error rejecting staged message:', error);
            toast.error('Failed to reject message', {
                persistNotification: true,
                notificationType: 'message_action',
            });
        } finally {
            setRejectingMessageId(null);
        }
    };

    const isApproving = (messageId: string) => approvingMessageId === messageId;
    const isRejecting = (messageId: string) => rejectingMessageId === messageId;

    return {
        handleApprove,
        handleReject,
        isApproving,
        isRejecting,
    };
}
